// Decline workflow stage 1 — "Propose Decline" modal.
//
// Reuses the AdminUndoConfirmModal shape: a red audit-visibility warning,
// a free-text reason textarea (server-validated ≥ 5 chars), and an
// explicit destructive confirm label. Proposing does NOT lock the deal —
// the body copy makes that clear so the proposer understands work
// continues until the LA ratifies.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useProposeDecline } from "@/hooks/deals/useDeclineWorkflow";

export const DECLINE_REASON_MIN_LENGTH = 5;

interface Props {
  open: boolean;
  dealId: string;
  dealNumber: string;
  onClose: () => void;
}

export function DeclineProposalModal({ open, dealId, dealNumber, onClose }: Props) {
  const toast = useToast();
  const propose = useProposeDecline(dealId);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= DECLINE_REASON_MIN_LENGTH;
  const showHint = reason.length > 0 && !reasonValid;

  async function submit() {
    if (!reasonValid || propose.isPending) return;
    try {
      await propose.mutateAsync({ reason: trimmed });
      toast.success("Decline proposed. The Loan Administrator will review it.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      open={open}
      onClose={propose.isPending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title="Propose Decline?"
      description={dealNumber}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={propose.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!reasonValid || propose.isPending}
            loading={propose.isPending}
          >
            CONFIRM: PROPOSE DECLINE
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <p className="font-semibold text-status-error" role="alert">
          ⚠ This action is audit-logged and visible to compliance reviewers.
        </p>
        <p className="text-small text-gray-dark">
          Proposing a decline flags the deal for the Loan Administrator's
          review. Work on the deal can continue while the proposal is
          pending. The deal becomes locked from edits only after the LA
          confirms the decline.
        </p>

        <label
          htmlFor="decline-propose-reason"
          className="block mt-space-2 text-small font-display uppercase tracking-label text-gold-shadow"
        >
          Reason for decline (audit-visible)
        </label>
        <textarea
          id="decline-propose-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-border-light rounded-md p-space-2 text-small focus:outline-none focus-visible:shadow-goldRing"
          placeholder={`Explain why this deal should be declined. Min. ${DECLINE_REASON_MIN_LENGTH} characters.`}
          rows={4}
          disabled={propose.isPending}
          aria-invalid={showHint || undefined}
        />
        {showHint && (
          <p className="text-caption text-status-error" role="alert">
            Reason must be at least {DECLINE_REASON_MIN_LENGTH} characters.
          </p>
        )}
      </div>
    </Modal>
  );
}
